import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Button } from "../components/ui/button";
import {
  ScrollText,
  CalendarIcon,
  User,
  Package,
  Truck,
  ArrowDownToLine,
  ArrowUpFromLine,
  Pencil,
  Trash2,
  Plus,
  RotateCcw,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

const AuditLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (entityTypeFilter) params.append("entity_type", entityTypeFilter);
      if (actionFilter) params.append("action", actionFilter);
      if (startDate) params.append("start_date", startDate.toISOString());
      if (endDate) params.append("end_date", endDate.toISOString());
      
      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
    } catch (error) {
      toast.error("Erro ao carregar logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [entityTypeFilter, actionFilter, startDate, endDate]);

  const formatDateTime = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy HH:mm:ss", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const formatDate = (date) => {
    try {
      return format(date, "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return "";
    }
  };

  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case "product":
        return <Package className="h-4 w-4" />;
      case "supplier":
        return <Truck className="h-4 w-4" />;
      case "user":
        return <User className="h-4 w-4" />;
      case "stock_entry":
        return <ArrowDownToLine className="h-4 w-4" />;
      case "stock_exit":
        return <ArrowUpFromLine className="h-4 w-4" />;
      default:
        return <ScrollText className="h-4 w-4" />;
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "create":
        return <Plus className="h-4 w-4 text-emerald-600" />;
      case "update":
        return <Pencil className="h-4 w-4 text-blue-600" />;
      case "delete":
        return <Trash2 className="h-4 w-4 text-red-600" />;
      case "restore":
        return <RotateCcw className="h-4 w-4 text-amber-600" />;
      default:
        return <ScrollText className="h-4 w-4" />;
    }
  };

  const getActionBadge = (action) => {
    switch (action) {
      case "create":
        return <Badge className="alert-badge-success">Criação</Badge>;
      case "update":
        return <Badge className="alert-badge-info">Atualização</Badge>;
      case "delete":
        return <Badge className="alert-badge-danger">Exclusão</Badge>;
      case "restore":
        return <Badge className="alert-badge-warning">Restauração</Badge>;
      default:
        return <Badge variant="secondary">{action}</Badge>;
    }
  };

  const getEntityLabel = (entityType) => {
    const labels = {
      product: "Produto",
      supplier: "Fornecedor",
      user: "Usuário",
      stock_entry: "Entrada",
      stock_exit: "Saída",
    };
    return labels[entityType] || entityType;
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Log de Auditoria
        </h1>
        <p className="text-slate-500 mt-1">
          Histórico de todas as ações realizadas no sistema
        </p>
      </div>

      {/* Filters */}
      <Card className="card-dashboard">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={entityTypeFilter || "all"} onValueChange={(v) => setEntityTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48" data-testid="entity-type-filter">
                <SelectValue placeholder="Tipo de Entidade" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="product">Produto</SelectItem>
                <SelectItem value="supplier">Fornecedor</SelectItem>
                <SelectItem value="user">Usuário</SelectItem>
                <SelectItem value="stock_entry">Entrada de Estoque</SelectItem>
                <SelectItem value="stock_exit">Saída de Estoque</SelectItem>
              </SelectContent>
            </Select>

            <Select value={actionFilter || "all"} onValueChange={(v) => setActionFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-full md:w-48" data-testid="action-filter">
                <SelectValue placeholder="Tipo de Ação" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="create">Criação</SelectItem>
                <SelectItem value="update">Atualização</SelectItem>
                <SelectItem value="delete">Exclusão</SelectItem>
                <SelectItem value="restore">Restauração</SelectItem>
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full md:w-auto gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {startDate ? formatDate(startDate) : "Data Inicial"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={startDate}
                  onSelect={setStartDate}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full md:w-auto gap-2">
                  <CalendarIcon className="h-4 w-4" />
                  {endDate ? formatDate(endDate) : "Data Final"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={setEndDate}
                  locale={ptBR}
                />
              </PopoverContent>
            </Popover>

            <Button
              variant="ghost"
              onClick={() => {
                setEntityTypeFilter("");
                setActionFilter("");
                setStartDate(null);
                setEndDate(null);
              }}
            >
              Limpar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="font-heading text-lg">
            Registros de Auditoria
          </CardTitle>
          <CardDescription>
            {logs.length} registro(s) encontrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <ScrollText className="h-12 w-12 mb-4 text-slate-300" />
              <p>Nenhum registro encontrado</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>Detalhes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="table-row-zebra">
                      <TableCell className="font-mono text-sm">
                        {formatDateTime(log.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-slate-400" />
                          <span>{log.user_name}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getActionIcon(log.action)}
                          {getActionBadge(log.action)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getEntityIcon(log.entity_type)}
                          <span>{getEntityLabel(log.entity_type)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-600">
                        {log.details || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
